import DateService from '@core/services/DateService';

type PlanCoverDatesSource = {
  [key: string]: unknown;
  coverEffectiveTo?: string | null;
  effectiveTo?: string | null;
  coverEffectiveFrom?: string | null;
  effectiveFrom?: string | null;
};

export function getPlanCoverEffectiveDates(fromPlan: PlanCoverDatesSource) {
  const planCoverEffectiveTo =
    fromPlan.coverEffectiveTo ?? fromPlan.effectiveTo ?? null;
  const planCoverEffectiveFrom =
    fromPlan.coverEffectiveFrom ?? fromPlan.effectiveFrom ?? null;

  return { planCoverEffectiveTo, planCoverEffectiveFrom };
}

export function hasPlanCoverEffectiveTo(
  planCoverEffectiveTo?: string | null
): boolean {
  return (
    planCoverEffectiveTo != null && String(planCoverEffectiveTo).trim() !== ''
  );
}

export function areDatesEqual(
  a?: string | null,
  b?: string | null
): boolean {
  if (!a && !b) return true;
  if (!a || !b) return false;

  const ta = new Date(a).getTime();
  const tb = new Date(b).getTime();

  return !Number.isNaN(ta) && !Number.isNaN(tb) && ta === tb;
}

/** Live defaults for a newly added cover row (IsNew = Yes). */
export function resolveNewCoverEffectiveDates(
  amendmentEffectiveDate?: string,
  policyExpiryDate?: string
) {
  return {
    coverEffectiveFrom: amendmentEffectiveDate || undefined,
    coverEffectiveTo: policyExpiryDate || undefined
  };
}

/** Cover Effective To default per endorsement Policy Covers spec. */
export function resolveCoverEffectiveTo({
  isRemoved,
  isNew,
  policyPlanCoverEffectiveTo,
  amendmentEffectiveDate,
  policyExpiryDate
}: {
  isRemoved: boolean;
  isNew: boolean;
  policyPlanCoverEffectiveTo?: string | null;
  amendmentEffectiveDate?: string;
  policyExpiryDate?: string;
}): string | undefined {
  if (isRemoved) {
    return amendmentEffectiveDate || undefined;
  }
  if (!isNew) {
    return policyPlanCoverEffectiveTo || undefined;
  }

  return policyExpiryDate || policyPlanCoverEffectiveTo || undefined;
}

/** Cover Effective From default per endorsement Policy Covers spec. */
export function resolveCoverEffectiveFrom({
  isNew,
  policyPlanCoverEffectiveFrom,
  amendmentEffectiveDate
}: {
  isNew: boolean;
  policyPlanCoverEffectiveFrom?: string | null;
  amendmentEffectiveDate?: string;
}): string | undefined {
  if (!isNew) {
    return policyPlanCoverEffectiveFrom || undefined;
  }

  return amendmentEffectiveDate || policyPlanCoverEffectiveFrom || undefined;
}

type CalculateNewPolicyCoverProRataRatioParams = {
  planCoverEffectiveTo?: string | null;
  planCoverEffectiveFrom?: string | null;
  coverEffectiveFrom?: string;
  coverEffectiveTo?: string;
  policyEffectiveFrom?: string;
  policyExpiryDate?: string;
};

/**
 * Pro-rata for a newly added policy cover (endorsement).
 * If PolicyPlanCover.CoverEffectiveTo is null:
 *   (AddMonths(PolicyEffectiveDate, 6) - CoverEffectiveFrom)
 *   / (AddMonths(PolicyEffectiveDate, 6) - PolicyEffectiveDate)
 * Otherwise:
 *   (CoverEffectiveTo - CoverEffectiveFrom) / (PolicyExpiryDate - PolicyEffectiveDate)
 */
export function calculateNewPolicyCoverProRataRatio({
  planCoverEffectiveTo,
  planCoverEffectiveFrom,
  coverEffectiveFrom,
  coverEffectiveTo,
  policyEffectiveFrom,
  policyExpiryDate
}: CalculateNewPolicyCoverProRataRatioParams): number {
  const d = (s?: string | null) => (s ? new Date(s).getTime() : NaN);
  const policyFrom = d(policyEffectiveFrom);

  if (!policyEffectiveFrom || Number.isNaN(policyFrom)) {
    return 1;
  }

  const coverFrom = d(coverEffectiveFrom);

  if (!hasPlanCoverEffectiveTo(planCoverEffectiveTo)) {
    if (Number.isNaN(coverFrom)) {
      return 1;
    }

    const windowEnd = DateService.addMonths(policyEffectiveFrom, 6).getTime();
    const top = Math.max(0, windowEnd - coverFrom);
    const bottom = Math.max(1, windowEnd - policyFrom);

    return +(top / bottom);
  }

  const coverTo = d(coverEffectiveTo);
  const policyTo = d(policyExpiryDate);

  if (
    Number.isNaN(coverTo) ||
    Number.isNaN(coverFrom) ||
    Number.isNaN(policyTo)
  ) {
    return 1;
  }

  const top = Math.max(0, coverTo - coverFrom);
  const bottom = Math.max(1, policyTo - policyFrom);

  return +(top / bottom);
}
